const DESKTOP_QUERY = '(min-width: 1100px)';
const DESKTOP_SCROLL_QUERY = '(min-width: 990px)';
const NATIVE_QUERY = '(max-width: 1099px)';
const IDLE_DELAY_MS = 400;
const GUARANTEE_COPY = '2 años de garantía';

const MOTION_CLASS = {
  forward: { enter: 'atelea-announcement-enter-right', exit: 'atelea-announcement-exit-left' },
  back: { enter: 'atelea-announcement-enter-left', exit: 'atelea-announcement-exit-right' },
};

const ALL_MOTION_CLASSES = [
  MOTION_CLASS.forward.enter,
  MOTION_CLASS.forward.exit,
  MOTION_CLASS.back.enter,
  MOTION_CLASS.back.exit,
];

/** @param {Element} element */
function clearAnnouncementMotion(element) {
  element.classList.remove(...ALL_MOTION_CLASSES);
}

/** @param {Element} bar */
function markGuaranteeSlides(bar) {
  bar.querySelectorAll('.announcement-bar__slide').forEach((slide) => {
    const text = slide.querySelector('.announcement-bar__text');
    slide.toggleAttribute(
      'data-atelea-italian-flag',
      text?.textContent?.trim() === GUARANTEE_COPY
    );
  });
}

class AteleaAnnouncementMotion {
  /** @param {HTMLElement} bar */
  constructor(bar) {
    this.bar = bar;
    this.slides = /** @type {HTMLElement[]} */ (
      Array.from(bar.querySelectorAll('.announcement-bar__slide'))
    );
    this.mediaQuery = window.matchMedia(NATIVE_QUERY);
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.currentIndex = this.findCurrentIndex();
    this.bound = false;
    this.observer = new MutationObserver((records) => this.handleMutations(records));
    this.handleMediaChange = () => this.applyMode();
    this.handleMotionChange = () => {
      if (this.reducedMotionQuery.matches) this.clearMotion();
    };
    /** @param {AnimationEvent} event */
    this.handleAnimationEnd = (event) => {
      if (event.target instanceof HTMLElement) clearAnnouncementMotion(event.target);
    };
  }

  init() {
    this.bar.addEventListener('animationend', this.handleAnimationEnd);
    this.mediaQuery.addEventListener('change', this.handleMediaChange);
    this.reducedMotionQuery.addEventListener('change', this.handleMotionChange);
    this.applyMode();
  }

  findCurrentIndex() {
    return this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
  }

  applyMode() {
    if (this.mediaQuery.matches) {
      this.bind();
      return;
    }

    this.unbind();
  }

  bind() {
    if (this.bound) return;

    this.currentIndex = this.findCurrentIndex();
    this.slides.forEach((slide) => {
      this.observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    });
    this.bound = true;
  }

  unbind() {
    if (this.bound) this.observer.disconnect();
    this.bound = false;
    this.clearMotion();
  }

  clearMotion() {
    this.slides.forEach(clearAnnouncementMotion);
  }

  /**
   * @param {number} from
   * @param {number} to
   * @returns {'forward' | 'back'}
   */
  directionBetween(from, to) {
    const total = this.slides.length;
    if (from < 0) return 'forward';
    if (to === (from - 1 + total) % total) return 'back';
    return 'forward';
  }

  /** @param {MutationRecord[]} records */
  handleMutations(records) {
    if (!this.mediaQuery.matches || this.reducedMotionQuery.matches) return;

    for (const record of records) {
      const slide = record.target;
      if (!(slide instanceof HTMLElement)) continue;
      if (slide.getAttribute('aria-hidden') !== 'false') continue;

      const nextIndex = this.slides.indexOf(slide);
      if (nextIndex === -1 || nextIndex === this.currentIndex) continue;

      const direction = this.directionBetween(this.currentIndex, nextIndex);
      const outgoing = this.slides[this.currentIndex];
      this.currentIndex = nextIndex;
      const motion = MOTION_CLASS[direction];

      clearAnnouncementMotion(slide);
      void slide.offsetWidth;
      slide.classList.add(motion.enter);

      if (outgoing && outgoing !== slide) {
        clearAnnouncementMotion(outgoing);
        void outgoing.offsetWidth;
        outgoing.classList.add(motion.exit);
      }
    }
  }

  destroy() {
    this.observer.disconnect();
    this.bar.removeEventListener('animationend', this.handleAnimationEnd);
    this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    this.reducedMotionQuery.removeEventListener('change', this.handleMotionChange);
    this.clearMotion();
  }
}

function getScrollEventTarget() {
  if (window.matchMedia(DESKTOP_SCROLL_QUERY).matches) {
    return document.querySelector('.page-wrapper') ?? document;
  }

  return document;
}

class AteleaAnnouncementRail {
  /** @param {HTMLElement} rail */
  constructor(rail) {
    this.rail = rail;
    this.content = rail.querySelector('.atelea-announcement-rail__content');
    this.toggle = rail.querySelector('.atelea-announcement-rail__toggle');
    this.mediaQuery = window.matchMedia(DESKTOP_QUERY);
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isDesktop = null;
    this.destroyed = false;
    this.pointerInside = false;
    this.focusInside = false;
    this.footerVisible = false;
    this.scrollTarget = null;
    /** @type {IntersectionObserver | null} */
    this.footerObserver = null;
    this.idleTimer = null;
    this.handleToggle = () => {
      this.cancelIdle();
      if (this.footerVisible) {
        this.setState('compact');
        return;
      }
      const state = this.rail.dataset.ateleaAnnouncementState;
      this.setState(state === 'compact' ? 'open' : 'compact');
    };
    this.handleScroll = () => this.handleScrollEvent();
    this.handleMediaChange = () => this.applyMode();
    this.handleMotionChange = () => {
      if (!this.mediaQuery.matches) return;
      this.cancelIdle();
      if (this.reducedMotionQuery.matches) {
        this.unbindScroll();
        this.setState(this.footerVisible ? 'compact' : 'open');
        return;
      }
      this.bindScroll();
    };
    this.handlePointerEnter = () => {
      this.pointerInside = true;
    };
    this.handlePointerLeave = () => {
      this.pointerInside = false;
    };
    this.handleFocusIn = () => {
      this.focusInside = true;
    };
    /** @param {FocusEvent} event */
    this.handleFocusOut = (event) => {
      const next = event.relatedTarget;
      this.focusInside = next instanceof Node && this.rail.contains(next);
    };
    /** @param {boolean} visible */
    this.handleFooterVisibility = (visible) => {
      this.footerVisible = visible;
      if (visible) {
        this.cancelIdle();
        this.setState('compact');
        return;
      }

      if (this.reducedMotionQuery.matches && this.mediaQuery.matches) this.setState('open');
    };
  }

  init() {
    if (!(this.content instanceof HTMLElement)) return;
    if (!(this.toggle instanceof HTMLButtonElement)) return;

    if (this.mediaQuery.matches) this.syncContent();
    this.toggle.addEventListener('click', this.handleToggle);
    this.rail.addEventListener('pointerenter', this.handlePointerEnter);
    this.rail.addEventListener('pointerleave', this.handlePointerLeave);
    this.rail.addEventListener('focusin', this.handleFocusIn);
    this.rail.addEventListener('focusout', this.handleFocusOut);
    this.mediaQuery.addEventListener('change', this.handleMediaChange);
    this.reducedMotionQuery.addEventListener('change', this.handleMotionChange);
    this.applyMode();
  }

  applyMode() {
    const desktop = this.mediaQuery.matches;
    if (desktop === this.isDesktop) return;

    this.isDesktop = desktop;
    if (desktop) {
      this.enterDesktop();
      return;
    }

    this.leaveDesktop();
  }

  enterDesktop() {
    if (!(this.content instanceof HTMLElement)) return;

    this.syncContent();
    this.bindFooterObserver();
    if (this.reducedMotionQuery.matches) {
      this.setState(this.footerVisible ? 'compact' : 'open');
      return;
    }

    this.setState('compact');
    window.requestAnimationFrame(() => {
      if (this.destroyed || !this.mediaQuery.matches || this.footerVisible) return;
      this.setState('open');
    });
    this.bindScroll();
  }

  syncContent() {
    if (!(this.content instanceof HTMLElement)) return;

    const nativeBar = document.querySelector('.atelea-announcement-native');
    if (!(nativeBar instanceof HTMLElement)) return;

    const items = [...nativeBar.querySelectorAll('.announcement-bar__slide')]
      .map((slide) => this.createItem(slide))
      .filter((item) => item instanceof HTMLElement);

    if (!items.length) return;

    this.content.replaceChildren();
    items.forEach((item, index) => {
      if (index > 0) {
        const separator = document.createElement('span');
        separator.className = 'atelea-announcement-rail__separator';
        separator.setAttribute('aria-hidden', 'true');
        this.content.append(separator);
      }
      this.content.append(item);
    });
  }

  /** @param {Element} slide */
  createItem(slide) {
    const text = slide.querySelector('.announcement-bar__text');
    if (!(text instanceof HTMLElement) || !text.textContent?.trim()) return null;

    const sourceLink = slide.querySelector('.announcement-bar__link');
    const item = document.createElement(sourceLink instanceof HTMLAnchorElement ? 'a' : 'span');
    item.className = 'atelea-announcement-rail__item';
    item.innerHTML = text.innerHTML.trim();
    if (text.textContent?.trim() === GUARANTEE_COPY) {
      item.setAttribute('data-atelea-italian-flag', '');
    }

    if (sourceLink instanceof HTMLAnchorElement) {
      const href = sourceLink.getAttribute('href');
      if (href) item.setAttribute('href', href);
    }

    const sourceStyle = slide.getAttribute('style');
    if (sourceStyle) item.setAttribute('style', sourceStyle);

    const sourceColor = slide.style.getPropertyValue('--color').trim();
    if (sourceColor) item.style.setProperty('--atl-announcement-color', sourceColor);

    return item;
  }

  leaveDesktop() {
    this.cancelIdle();
    this.unbindScroll();
    this.unbindFooterObserver();
    this.footerVisible = false;
    this.rail.removeAttribute('data-atelea-announcement-state');
    if (this.content instanceof HTMLElement) {
      this.content.removeAttribute('inert');
      this.content.setAttribute('aria-hidden', 'false');
    }
    if (this.toggle instanceof HTMLButtonElement) {
      this.toggle.textContent = '›';
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', 'Ocultar información de compra');
    }
  }

  /** @param {'open' | 'compact'} state */
  setState(state) {
    const compact = state === 'compact';
    this.rail.dataset.ateleaAnnouncementState = state;

    if (this.content instanceof HTMLElement) {
      this.content.toggleAttribute('inert', compact);
      this.content.setAttribute('aria-hidden', `${compact}`);
    }

    if (this.toggle instanceof HTMLButtonElement) {
      this.toggle.textContent = compact ? '‹' : '›';
      this.toggle.setAttribute('aria-expanded', `${!compact}`);
      this.toggle.setAttribute(
        'aria-label',
        compact ? 'Mostrar información de compra' : 'Ocultar información de compra'
      );
    }
  }

  handleScrollEvent() {
    if (!this.mediaQuery.matches || this.reducedMotionQuery.matches) return;

    if (this.footerVisible) {
      this.cancelIdle();
      this.setState('compact');
      return;
    }

    if (!this.pointerInside && !this.focusInside) this.setState('compact');
    this.cancelIdle();
    this.idleTimer = window.setTimeout(() => {
      if (this.pointerInside || this.focusInside) return;
      this.setState('open');
    }, IDLE_DELAY_MS);
  }

  bindScroll() {
    if (this.scrollTarget || this.reducedMotionQuery.matches) return;
    this.scrollTarget = getScrollEventTarget();
    this.scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  unbindScroll() {
    if (!this.scrollTarget) return;
    this.scrollTarget.removeEventListener('scroll', this.handleScroll);
    this.scrollTarget = null;
  }

  bindFooterObserver() {
    this.unbindFooterObserver();
    if (typeof IntersectionObserver === 'undefined') return;

    const footer = document.querySelector('footer');
    if (!(footer instanceof HTMLElement)) return;

    this.footerObserver = new IntersectionObserver(
      ([entry]) => this.handleFooterVisibility(entry.isIntersecting),
      { threshold: 0 }
    );
    this.footerObserver.observe(footer);
  }

  unbindFooterObserver() {
    this.footerObserver?.disconnect();
    this.footerObserver = null;
  }

  cancelIdle() {
    if (this.idleTimer !== null) window.clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  destroy() {
    this.destroyed = true;
    this.cancelIdle();
    this.unbindScroll();
    this.unbindFooterObserver();
    this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    this.reducedMotionQuery.removeEventListener('change', this.handleMotionChange);
    this.rail.removeEventListener('pointerenter', this.handlePointerEnter);
    this.rail.removeEventListener('pointerleave', this.handlePointerLeave);
    this.rail.removeEventListener('focusin', this.handleFocusIn);
    this.rail.removeEventListener('focusout', this.handleFocusOut);
    this.toggle?.removeEventListener('click', this.handleToggle);
  }
}

/** @type {Map<HTMLElement, AteleaAnnouncementRail>} */
const railControllers = new Map();

/** @type {Map<HTMLElement, AteleaAnnouncementMotion>} */
const motionControllers = new Map();

/**
 * @param {Document | Element} root
 */
function start(root = document) {
  const rails =
    root instanceof Element && root.matches('.atelea-announcement-rail')
      ? [root]
      : [...root.querySelectorAll('.atelea-announcement-rail')];
  for (const rail of rails) {
    if (railControllers.has(rail)) continue;
    const controller = new AteleaAnnouncementRail(rail);
    railControllers.set(rail, controller);
    controller.init();
  }
}

/**
 * @param {Document | Element} root
 */
function startMotion(root = document) {
  const bars =
    root instanceof Element && root.matches('announcement-bar-component')
      ? [root]
      : [...root.querySelectorAll('announcement-bar-component')];
  for (const bar of bars) {
    if (motionControllers.has(bar)) continue;
    markGuaranteeSlides(bar);
    const slides = bar.querySelectorAll('.announcement-bar__slide');
    if (slides.length < 2) continue;
    const controller = new AteleaAnnouncementMotion(bar);
    motionControllers.set(bar, controller);
    controller.init();
  }
}

/** @param {Element} root */
function cleanup(root) {
  for (const [rail, controller] of railControllers) {
    if (root !== rail && !root.contains(rail)) continue;
    controller.destroy();
    railControllers.delete(rail);
  }
}

/** @param {Document | Element} root */
function cleanupMotion(root) {
  for (const [bar, controller] of motionControllers) {
    if (root !== bar && !root.contains(bar)) continue;
    controller.destroy();
    motionControllers.delete(bar);
  }
}

start();
startMotion();
document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;
  start(event.target);
  startMotion(event.target);
});
document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return;
  cleanup(event.target);
  cleanupMotion(event.target);
});
